import { observer } from "mobx-react-lite";
import { useMemo } from "react";

import blocks, { BlockType } from "../../..";

import { CodeExamples } from "./code-block-examples";
import { useCodeLanguage, useCodeMonacoCompletion } from "./hooks";

import CodeEditor from "@/components/CodeEditor";
import { languages } from "@/components/CodeEditor/constants";
import { CodeExecutionConfiguration } from "@/types/evaluate";
import { BlockProps } from "@/components/PowerBlock/Block";
import { withBlockStore } from "@/components/PowerBlock/Block/block-store";
import { createEmptySources, flattenSources } from "@/components/PowerBlock/Block/hooks";
import { useIsReadonly } from "@/components/PowerBlock/Block/readonly-context";
import SectionGroup from "@/components/PowerBlock/Block/SectionGroup";
import ValidatedField from "@/components/PowerBlock/Block/ValidatedField";
import { required } from "@/components/PowerBlock/Block/validation/rules";
import { createBlockSchema } from "@/components/PowerBlock/Block/validation/schemaBuilder";

export { CodeExamples } from "./code-block-examples";

const validationSchema = createBlockSchema<CodeExecutionConfiguration>()
  .requireName()
  .addRule(required("configuration.code", "Code is required"))
  .build();

const CodeBlockCore = observer(
  ({
    sources = createEmptySources(),
  }: Omit<BlockProps<CodeExecutionConfiguration>, "DefaultFields">) => {
    const isReadonly = useIsReadonly();
    const flattenedSources = flattenSources(sources);

    const { language, code, setCode, handleLanguageChange, handleExampleChange, isCustomCode } =
      useCodeLanguage();

    useCodeMonacoCompletion(language, flattenedSources);

    const editorOptions = useMemo(
      () => ({
        readOnly: isReadonly,
      }),
      [isReadonly],
    );

    const CodeIcon = useMemo(() => blocks[BlockType.CODE_EXECUTION].icon!, []);

    return (
      <SectionGroup
        title="Code Configuration"
        description="Write and configure code that executes for each row"
        icon={<CodeIcon className="h-4 w-4" />}
      >
        <ValidatedField path="configuration.code" variant="sources">
          <div className="space-y-4">
            <div className="text-sm text-gray-600">
              Write code that will execute for each row. Access data through the `data` variable.
              Return the cell value. Stdout will be ignored.
            </div>

            <CodeEditor
              languages={languages}
              selectedLanguage={language}
              editorOptions={editorOptions}
              languageSelectorLabel="Programming Language"
              code={code}
              dataIsValid={{ code: true }}
              controlsType="dropdown"
              handleCodeChange={setCode}
              handleLanguageChange={handleLanguageChange}
            />

            {isCustomCode && !isReadonly && (
              <button
                onClick={handleExampleChange}
                className="text-sm text-blue-600 underline hover:text-blue-800"
              >
                Reset to example code
              </button>
            )}
          </div>
        </ValidatedField>
      </SectionGroup>
    );
  },
);

export default withBlockStore<CodeExecutionConfiguration>(CodeBlockCore, {
  validationSchema,
  defaultConfiguration: {
    language: "PYTHON",
    code: CodeExamples.PYTHON,
  },
});
